interface KvaBanners {
  hideBanner: () => void
  showBanner: (
    appGuid: string,
    location: string,
    targetingNetworkType?: string,
    options?: { zIndex?: number }
  ) => void
}

declare global {
  interface Window {
    KvaBanners?: { new(): KvaBanners }
  }
}

const normalizeUrl = (url: string) => url.split('?')[0].split('#')[0].replace(/\/$/, '').toLowerCase()

export default defineNuxtPlugin(() => {
  const { $feature } = useNuxtApp()
  const router = useRouter()
  const locale = useLocale()
  const isDev = useRuntimeConfig().public.targetEnv !== 'PROD'
  const config = $feature.configKochavaSmartBanner
  if (!config) return

  const { appGuid, embedUrl, targetUrls, excludedUrls } = config
  const normalizedTargetUrls = (targetUrls || []).map(normalizeUrl)
  const normalizedExcludedUrls = (excludedUrls || []).map(normalizeUrl)
  const bannerOptions = { zIndex: 800 }

  const bannerLocationByLocale: Record<string, { targeted: string, default: string }> = isDev
    ? {
        'en-us': { targeted: 'showTargetedBanner-PREPROD', default: 'showDefaultBanner-PREPROD' },
        'en-ca': { targeted: 'showTargetedBannerEN-CA-PREPROD', default: 'showDefaultBannerEN-CA-PREPROD' },
        'fr-ca': { targeted: 'showTargetedBannerFR-PREPROD', default: 'showDefaultBannerFR-PREPROD' },
      }
    : {
        'en-us': { targeted: 'showTargetedBanner-PROD', default: 'showDefaultBanner-PROD' },
        'en-ca': { targeted: 'showTargetedBannerEN-CA-PROD', default: 'showDefaultBannerEN-CA-PROD' },
        'fr-ca': { targeted: 'showTargetedBannerFR-PROD', default: 'showDefaultBannerFR-PROD' },
      }
  const bannerLocation = bannerLocationByLocale[locale] ?? bannerLocationByLocale['en-us']

  let kvaBanners: KvaBanners | null = null

  const toggleBanner = () => {
    if (!kvaBanners) return

    if (normalizedExcludedUrls.includes(normalizeUrl(window.location.href))) {
      kvaBanners.hideBanner()
      return
    }

    const isTargeted = normalizedTargetUrls.includes(normalizeUrl(window.location.href))
    kvaBanners.showBanner(
      appGuid,
      isTargeted ? bannerLocation.targeted : bannerLocation.default,
      undefined,
      bannerOptions,
    )
  }

  useHead({
    script: [{
      src: embedUrl,
      onload: () => {
        try {
          if (typeof window.KvaBanners === 'function') {
            kvaBanners = new window.KvaBanners()
            toggleBanner()
          }
        }
        catch (err) {
          log.error(err)
        }
      },
      onerror: () => {
        log.error(`Failed to load Kochava SDK: ${embedUrl}`)
      }
    }]
  })

  onScopeDispose(router.afterEach(toggleBanner))
})
